import Phaser from 'phaser';
import { COLORS, TILE_SIZE } from '../utils/constants';
import { TOWER_DEFS, TOWER_TYPES_ORDERED } from '../data/towers';
import { ENEMY_DEFS, BOSS_DEFS } from '../data/enemies';
import { generateBlobTextures, generateBlobTexturesFromSource, generatePathOverlayTextures, generatePathOverlayTexturesFromSource } from '../systems/BlobTileset';
import { generateTransitionTextures, generateTransitionAlphaMasks } from '../systems/TerrainTransition';

export class BootScene extends Phaser.Scene {
  /** Set of tile keys that were loaded from AI-generated images (vs procedural). */
  private aiLoadedTiles = new Set<string>();

  /** Terrain keys that have a complete Wang tile set (16 tiles 0-15). */
  private wangTerrainKeys = new Set<string>();

  constructor() { super('BootScene'); }

  preload() {
    // ── Try to load AI-generated tile images from assets/tiles/ ─────────
    // Missing images are silently skipped; procedural fallback runs in create().
    // Transition tiles (tile_trans_*) are NOT preloaded — they are always
    // generated procedurally in generateTerrainTransitionTextures().
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[Boot] AI tile not found: ${file.key} → generating procedurally`);
    });
    this.load.on('filecomplete', (_key: string) => {
      // Wang tile variants (tile_*_wang_N) are NOT AI tiles — they are
      // pre-generated seamless variants. Don't add them to aiLoadedTiles
      // or they will be incorrectly sliced into crop sub-textures.
      if (!/_wang_\d+$/.test(_key)) {
        this.aiLoadedTiles.add(_key);
      }
    });

    // ── Boot-loop kick: If animation frames don't fire (e.g. headless
    // or slow environments), manually drain the Loader and process scene
    // transitions on a timer so the game never stalls. ──────────────────
    const kickBoot = () => {
      const ld = this.load;
      const sm = this.sys.game.scene;
      // 1) Keep the Loader moving
      if (ld.state === 1 && ld.list.size > 0 && ld.inflight.size < ld.maxParallelDownloads) {
        ld.update();
      }
      // 2) Run a scene step so the Loader's update listener fires too
      this.sys.step(performance.now(), 16);
      // 3) Process any queued scene transitions
      sm.processQueue();
      // Keep kicking until Loader is done AND the next scene is running
      if (ld.state !== 3 || sm.isProcessing || sm.getScene('MenuScene')?.sys.settings.status !== 5) {
        setTimeout(kickBoot, 100);
      }
    };
    setTimeout(kickBoot, 250);

    // Base terrain tiles
    this.load.image('tile_water',    'assets/tiles/tile_water.png');
    this.load.image('tile_grass',    'assets/tiles/tile_grass.png');
    this.load.image('tile_sand',     'assets/tiles/tile_sand.png');
    this.load.image('tile_buildable','assets/tiles/tile_grass.png'); // alias
    this.load.image('tile_ground',   'assets/tiles/tile_water.png'); // alias

    // Path / road tiles
    this.load.image('tile_path',     'assets/tiles/tile_path.png');

    // Special tiles
    this.load.image('tile_spawn',    'assets/tiles/tile_spawn.png');
    this.load.image('tile_goal',     'assets/tiles/tile_goal.png');

    // ── Wang tile sets (16 per terrain, pre-generated by tools/wang-tiles/) ──
    // Each Wang tile is 192×192, GPU-downscaled to TILE_SIZE (48×48) for
    // smooth display with linear filtering (pixelArt: false).
    // Stored in subfolders: assets/tiles/tile_grass_wang/wang_0.png … wang_15.png
    // Missing sets are silently skipped; the crop-based fallback is used instead.
    const TERRAIN_KEYS = ['tile_water', 'tile_grass', 'tile_sand', 'tile_path'];
    for (const baseKey of TERRAIN_KEYS) {
      for (let i = 0; i < 16; i++) {
        this.load.image(`${baseKey}_wang_${i}`, `assets/tiles/${baseKey}_wang/wang_${i}.png`);
      }
    }

    // Tower textures (AI-generated, optional — procedural fallback in create())
    for (const type of TOWER_TYPES_ORDERED) {
      for (let level = 0; level <= 2; level++) {
        this.load.image(`tower_${type}_${level}`, `assets/tiles/tower_${type}_${level}.png`);
      }
      for (const evo of TOWER_DEFS[type].evolutions) {
        this.load.image(`tower_${evo.type}`, `assets/tiles/tower_${evo.type}.png`);
      }
    }

    // ── City decoration textures (AI-generated, optional — procedural fallback in create())
    this.load.image('deco_city_dense',    'assets/tiles/deco_city_dense.png');
    this.load.image('deco_city_market',   'assets/tiles/deco_city_market.png');
    this.load.image('deco_city_harbor',   'assets/tiles/deco_city_harbor.png');
    this.load.image('deco_city_house_01', 'assets/tiles/deco_city_house_01.png');
    this.load.image('deco_city_house_02', 'assets/tiles/deco_city_house_02.png');
    this.load.image('deco_city_tower',    'assets/tiles/deco_city_tower.png');

    // ── Ability icons (AI-generated, optional — procedural fallback in create())
    this.load.image('ability_freeze',           'assets/tiles/ability_freeze.png');
    this.load.image('ability_meteor',           'assets/tiles/ability_meteor.png');
    this.load.image('ability_lightning_storm',  'assets/tiles/ability_lightning_storm.png');
    this.load.image('ability_heal_aura',        'assets/tiles/ability_heal_aura.png');

    // ── Economy building textures (AI-generated, optional — procedural fallback in create())
    //    Upgrade variants: eco_{type}_1 (Mk II), eco_{type}_2 (Mk III)
    const ECO_TYPES = ['farm', 'mill', 'bakery', 'fishery', 'harbor', 'warehouse', 'market', 'lighthouse'];
    for (const ecoType of ECO_TYPES) {
      this.load.image(`eco_${ecoType}`, `assets/tiles/eco_${ecoType}.png`);
      this.load.image(`eco_${ecoType}_1`, `assets/tiles/eco_${ecoType}_1.png`);
      this.load.image(`eco_${ecoType}_2`, `assets/tiles/eco_${ecoType}_2.png`);
    }
    this.load.image('eco_cart',       'assets/tiles/eco_cart.png');
    this.load.image('eco_fisher_ship', 'assets/tiles/eco_fisher_ship.png');
  }

  create() {
    this.fillMissingTileTextures();
    this.generateUITileTextures();
    this.generateAbilityIcons();

    // Detect which terrain keys have complete Wang tile sets (all 16 loaded)
    this.detectWangTileSets();

    this.generateMipmapsForAITiles();
    this.downscaleSpriteIcons();
    this.generateBlobTextures();
    // If an AI path tile was loaded, generate masked blob textures from it
    if (this.textures.exists('tile_path')) {
      generateBlobTexturesFromSource(this, 'tile_path', 'tile_path_blob');
      generatePathOverlayTexturesFromSource(this, 'tile_path', 'tile_path_blob');
    }
    this.generateTerrainTransitionTextures();
    this.generateTowerTextures();
    this.generateEnemySheets();
    this.generateHeroSheet();
    this.generateProjectileTextures();
    this.generateParticleTextures();
    this.generateCityDecorTextures();
    this.generateEconomyTextures();
    this.generateUITextures();
    this.registerAnimations();

    // Share AI tile keys + Wang tile availability so GameScene can use them
    this.game.registry.set('aiTiles', [...this.aiLoadedTiles]);
    this.game.registry.set('wangTerrainKeys', [...this.wangTerrainKeys]);

    this.scene.start('MenuScene');
  }

  /** Generate procedural textures for any tiles not loaded from disk. */
  private fillMissingTileTextures() {
    if (!this.textures.exists('tile_water')) this.generateWaterTile();
    if (!this.textures.exists('tile_ground')) this.generateWaterTileAlias();
    if (!this.textures.exists('tile_grass')) this.generateGrassTile();
    if (!this.textures.exists('tile_buildable')) this.generateGrassTileAlias();
    if (!this.textures.exists('tile_sand')) this.generateSandTile();
    if (!this.textures.exists('tile_path')) this.generatePathTile();
    if (!this.textures.exists('tile_spawn')) this.generateSpawnTile();
    if (!this.textures.exists('tile_goal')) this.generateGoalTile();
  }

  /**
   * Detect which terrain keys have a complete Wang tile set.
   * A set is complete if all 16 tiles (`{key}_wang_0` through `{key}_wang_15`)
   * were loaded from disk without error.
   */
  private detectWangTileSets(): void {
    const TERRAIN_KEYS = ['tile_water', 'tile_grass', 'tile_sand', 'tile_path'];
    for (const baseKey of TERRAIN_KEYS) {
      const complete = this.textures.exists(`${baseKey}_wang_0`)
        && this.textures.exists(`${baseKey}_wang_15`);
      if (complete) {
        this.wangTerrainKeys.add(baseKey);
        console.log(`[Boot] Wang tile set detected: ${baseKey}`);
      }
    }
  }

  /**
   * Downscale oversized AI tile textures.
   *
   * Always downscale to DOWNSAMPLE_SIZE so that:
   *   1. Transition textures (which bake the AI source into 192px SDF masks)
   *      have a high-quality source to sample from.
   *   2. Crop sub-textures (non-Wang fallback) have enough pixel data for
   *      variation.
   *
   * The actual tile rendering uses the pre-generated 48×48 Wang tiles from
   * disk (when available), so the AI texture doesn't need to be at TILE_SIZE.
   */
  private generateMipmapsForAITiles(): void {
    const DOWNSAMPLE_SIZE = 256;

    /**
     * Get a 2D context with explicit high-quality smoothing enabled.
     * Fresh canvas contexts default to imageSmoothingEnabled=true and
     * imageSmoothingQuality='low', but Phaser's pixelArt mode may have
     * patched the CanvasRenderingContext2D prototype.  Being explicit
     * guarantees we always get interpolated (not nearest-neighbour)
     * downscaling on the CPU side.
     */
    const smoothCtx = (w: number, h: number): CanvasRenderingContext2D => {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      return ctx;
    };

    for (const key of this.aiLoadedTiles) {
      // Ability icons and tower sprites get their own downscale pass below —
      // this method only handles terrain/base tiles that need crop slicing.
      if (key.startsWith('ability_') || key.startsWith('tower_') || key.startsWith('deco_')) continue;

      const texture = this.textures.get(key);
      if (!texture) continue;

      const source = texture.source[0];
      const img = source?.image as HTMLImageElement | undefined;
      if (!img) continue;

      // Always use DOWNSAMPLE_SIZE — even if Wang tiles exist.  The AI
      // texture is used as a source for transition texture generation and
      // needs sufficient resolution to avoid blur when baked into the
      // 192px SDF mask.
      const targetSize = DOWNSAMPLE_SIZE;
      const needsSlice = !this.wangTerrainKeys.has(key);

      let finalCanvas: HTMLCanvasElement;
      const maxDim = Math.max(img.width, img.height);

      if (maxDim > targetSize) {
        // ── Multi-step CPU downscale ─────────────────────────────────────
        // Strategy: never downscale by more than 2× in a single drawImage
        // call, and always use explicit high-quality bilinear interpolation.
        // This produces visibly sharper results than a single large step
        // because each halving pass preserves more mid-frequency detail.
        let src: TexImageSource = img;
        let curW = img.width;
        let curH = img.height;

        // Halve until we're ≤ 2× the target, then one final step
        while (Math.max(curW, curH) > targetSize * 2) {
          const nextW = Math.round(curW / 2);
          const nextH = Math.round(curH / 2);
          const stepCtx = smoothCtx(nextW, nextH);
          stepCtx.drawImage(src, 0, 0, nextW, nextH);
          src = stepCtx.canvas;
          curW = nextW;
          curH = nextH;
        }

        // Final step: clamp to targetSize while preserving aspect ratio
        const finalScale = targetSize / Math.max(curW, curH);
        const dw = Math.round(curW * finalScale);
        const dh = Math.round(curH * finalScale);
        const ctx = smoothCtx(dw, dh);
        ctx.drawImage(src, 0, 0, dw, dh);
        finalCanvas = ctx.canvas;

        // Replace the huge original with the downscaled version
        this.textures.remove(key);
        this.textures.addCanvas(key, finalCanvas);
      } else {
        // Already small enough — extract a canvas copy
        const ctx = smoothCtx(img.width, img.height);
        ctx.drawImage(img, 0, 0);
        finalCanvas = ctx.canvas;
      }

      // Slice into crop sub-textures (only for non-Wang keys that need per-cell variation)
      if (needsSlice) {
        this.sliceCropTextures(key, finalCanvas);
      }
    }
  }

  /**
   * Slice a canvas into a grid of TILE_SIZE × TILE_SIZE sub-textures.
   * Each sub-texture is registered as `{baseKey}_crop_N`.
   */
  private sliceCropTextures(baseKey: string, canvas: HTMLCanvasElement): void {
    const cols = Math.floor(canvas.width / TILE_SIZE);
    const rows = Math.floor(canvas.height / TILE_SIZE);
    if (cols < 1 || rows < 1) return;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const index = r * cols + c;
        const cropKey = `${baseKey}_crop_${index}`;
        if (this.textures.exists(cropKey)) this.textures.remove(cropKey);

        // Each slice needs its own canvas — addCanvas stores a reference
        const slice = document.createElement('canvas');
        slice.width = TILE_SIZE;
        slice.height = TILE_SIZE;
        const sctx = slice.getContext('2d')!;
        sctx.drawImage(
          canvas,
          c * TILE_SIZE, r * TILE_SIZE, TILE_SIZE, TILE_SIZE,
          0, 0, TILE_SIZE, TILE_SIZE,
        );
        this.textures.addCanvas(cropKey, slice);
      }
    }
  }

  /**
   * Downscale ability icons and other sprite textures to a display-appropriate
   * size using multi-step high-quality bilinear interpolation.  The terrain
   * mipmap pass targets 256px (for crop slicing), but sprite icons displayed
   * at ~33–50px benefit from a much smaller source to avoid the blur that
   * comes from extreme WebGL downscaling.
   */
  private downscaleSpriteIcons(): void {
    // Per-key target sizes (max dimension).  Ability icons & tower sprites
    // display at ~33–50px so 96px is 2–3× headroom.  Larger decorations
    // (dense city cluster, harbor panorama) need more resolution to stay
    // sharp when rendered at their native sizes on the map.
    const targetForKey = (key: string): number => {
      if (key === 'deco_city_dense' || key === 'deco_city_harbor') return 292;
      return 192;
    };

    const smoothCtx = (w: number, h: number): CanvasRenderingContext2D => {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      return ctx;
    };

    const keys = [...this.aiLoadedTiles].filter(k =>
      k.startsWith('ability_') || k.startsWith('tower_') || k.startsWith('deco_'),
    );

    for (const key of keys) {
      const texture = this.textures.get(key);
      if (!texture) continue;

      const source = texture.source[0];
      const img = source?.image as HTMLImageElement | undefined;
      if (!img) continue;

      const TARGET = targetForKey(key);
      const maxDim = Math.max(img.width, img.height);
      if (maxDim <= TARGET) continue; // already small enough

      // Multi-step halving for maximum quality, same approach as terrain mipmaps
      let src: TexImageSource = img;
      let curW = img.width;
      let curH = img.height;

      // Halve until ≤ 2× target
      while (Math.max(curW, curH) > TARGET * 2) {
        const nextW = Math.round(curW / 2);
        const nextH = Math.round(curH / 2);
        const stepCtx = smoothCtx(nextW, nextH);
        stepCtx.drawImage(src, 0, 0, nextW, nextH);
        src = stepCtx.canvas;
        curW = nextW;
        curH = nextH;
      }

      // Final step: clamp to TARGET, preserve aspect ratio
      const scale = TARGET / Math.max(curW, curH);
      const dw = Math.round(curW * scale);
      const dh = Math.round(curH * scale);
      const ctx = smoothCtx(dw, dh);
      ctx.drawImage(src, 0, 0, dw, dh);

      // Replace the oversized texture with the compact version
      this.textures.remove(key);
      this.textures.addCanvas(key, ctx.canvas);
    }
  }

  /** Generate UI tile overlays that are always procedural (never AI). */
  private generateUITileTextures() {
    const g = this.add.graphics();
    const S = TILE_SIZE;

    // Buildable hover – green overlay
    g.clear();
    g.fillStyle(0x0a2a1a, 1);
    g.fillRect(0, 0, S, S);
    g.lineStyle(2, 0x44ff44, 0.8);
    g.strokeRect(1, 1, S - 2, S - 2);
    g.fillStyle(0x44ff44, 0.06);
    g.fillRect(2, 2, S - 4, S - 4);
    g.generateTexture('tile_buildable_hover', S, S);

    // Edge transition wall
    g.clear();
    for (let y = 0; y < S; y++) {
      if (y < S * 0.4) {
        g.fillStyle(0x4a8a3a, 0.9);
      } else {
        const r = Math.floor((0.35 + (y / S) * 0.10) * 255);
        const gr = Math.floor((0.28 + (y / S) * 0.12) * 255);
        const b = Math.floor((0.18 + (y / S) * 0.08) * 255);
        g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      }
      g.fillRect(0, y, S, 1);
    }
    for (let x = 2; x < S - 2; x += 6) {
      g.fillStyle(0x888877, 0.4);
      g.fillCircle(x, S * 0.38, 1.5 + Math.sin(x * 0.5));
    }
    g.generateTexture('tile_edge', S, S);

    // Wall
    g.clear();
    g.fillStyle(0x5a4a30, 1);
    g.fillRect(0, 0, S, S);
    g.fillStyle(0x8a7a5a, 0.5);
    g.fillRect(0, 1, S, 3);
    g.fillRect(1, 0, 3, S);
    g.generateTexture('tile_wall', S, S);

    g.destroy();
  }

  // ─── Individual tile generators (procedural fallback) ──────────────────

  private generateWaterTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const r = 0.12 + (0.14 - 0.12) * (t < 0.33 ? t * 3 : t < 0.66 ? 1 - (t - 0.33) * 3 : (t - 0.66) * 3);
      const gr = 0.22 + (0.28 - 0.22) * Math.sin(t * Math.PI);
      const b  = 0.48 + (0.55 - 0.48) * Math.sin(t * Math.PI);
      g.fillStyle(Phaser.Display.Color.GetColor(Math.floor(r * 255), Math.floor(gr * 255), Math.floor(b * 255)), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let row = 0; row < 3; row++) {
      const baseY = S * (0.25 + row * 0.22);
      g.lineStyle(1.5, 0x66bbee, 0.40);
      g.beginPath();
      for (let x = 0; x < S; x++) {
        const phase = (x / S) * Math.PI * 2 + row * 1.7;
        const wy = baseY + 2.5 * Math.sin(phase);
        if (x === 0) g.moveTo(x, wy); else g.lineTo(x, wy);
      }
      g.strokePath();
    }
    for (let row = 0; row < 3; row++) {
      const baseY = S * (0.25 + row * 0.22);
      g.fillStyle(0x99ddff, 0.22);
      for (let i = 0; i < 4; i++) {
        const fx = (i * 13 + row * 7) % S;
        const wy = baseY + 2.5 * Math.sin((fx / S) * Math.PI * 2 + row * 1.7);
        g.fillEllipse(fx, wy, 5, 2);
      }
    }
    g.generateTexture('tile_water', S, S);
    g.destroy();
  }

  private generateWaterTileAlias() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const r = 0.12 + 0.02 * Math.sin(t * Math.PI * 2);
      const gr = 0.22 + 0.06 * Math.sin(t * Math.PI);
      const b = 0.48 + 0.07 * Math.sin(t * Math.PI);
      g.fillStyle(Phaser.Display.Color.GetColor(Math.floor(r * 255), Math.floor(gr * 255), Math.floor(b * 255)), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let row = 0; row < 3; row++) {
      const baseY = S * (0.25 + row * 0.22);
      g.lineStyle(1.5, 0x66bbee, 0.40);
      g.beginPath();
      for (let x = 0; x < S; x++) {
        const wy = baseY + 2.5 * Math.sin((x / S) * Math.PI * 2 + row * 1.7);
        if (x === 0) g.moveTo(x, wy); else g.lineTo(x, wy);
      }
      g.strokePath();
    }
    g.generateTexture('tile_ground', S, S);
    g.generateTexture('tile_blocked', S, S);
    g.destroy();
  }

  private generateGrassTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const osc = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 - Math.PI / 2);
      const r = Math.floor((0.14 + 0.04 * osc) * 255);
      const gr = Math.floor((0.34 + 0.10 * osc) * 255);
      const b = Math.floor((0.10 + 0.03 * osc) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let i = 0; i < 16; i++) {
      const gx = (i * 17 + 5) % S;
      const gy = (i * 13 + 7) % S;
      g.fillStyle(0x3a8a22, (i % 3) === 0 ? 0.5 : 0.35);
      g.fillRect(gx, gy, 2, 5 + (i % 4));
    }
    for (let i = 0; i < 10; i++) {
      g.fillStyle(0x5aaa33, 0.25);
      g.fillRect((i * 23 + 3) % S, (i * 19 + 11) % S, 1, 3);
    }
    const fps = [[0.15,0.12],[0.55,0.08],[0.35,0.42],[0.85,0.28],[0.10,0.62],[0.65,0.72],[0.45,0.88],[0.88,0.55],[0.72,0.92]];
    for (const [fx, fy] of fps) {
      g.fillStyle(Math.random() > 0.5 ? 0xffdd44 : 0xff9933, 0.45);
      g.fillCircle(fx * S, fy * S, 1.2);
    }
    g.generateTexture('tile_grass', S, S);
    g.destroy();
  }

  private generateGrassTileAlias() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const osc = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 - Math.PI / 2);
      const r = Math.floor((0.14 + 0.04 * osc) * 255);
      const gr = Math.floor((0.34 + 0.10 * osc) * 255);
      const b = Math.floor((0.10 + 0.03 * osc) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let i = 0; i < 16; i++) {
      g.fillStyle(0x3a8a22, (i % 3) === 0 ? 0.5 : 0.35);
      g.fillRect((i * 17 + 5) % S, (i * 13 + 7) % S, 2, 5 + (i % 4));
    }
    for (let i = 0; i < 10; i++) {
      g.fillStyle(0x5aaa33, 0.25);
      g.fillRect((i * 23 + 3) % S, (i * 19 + 11) % S, 1, 3);
    }
    for (let side = 0; side < 4; side++) {
      const sx2 = side < 2 ? 4 : S - 4;
      const sy2 = (side % 2 === 0) ? 4 : S - 4;
      g.fillStyle(0x5a3a1a, 0.7);
      g.fillRect(sx2 - 1, sy2 - 2, 2, 4);
      g.fillStyle([0xff4444, 0x44ff44, 0x4444ff, 0xffff44][side], 0.6);
      g.fillCircle(sx2, sy2 - 2, 2);
    }
    g.lineStyle(1, 0x0a1808, 0.2);
    g.strokeRect(0, 0, S, S);
    g.generateTexture('tile_buildable', S, S);
    g.destroy();
  }

  private generateSandTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const r = Math.floor((0.70 + 0.08 * Math.sin(t * Math.PI)) * 255);
      const gr = Math.floor((0.62 + 0.06 * Math.sin(t * Math.PI + 0.5)) * 255);
      const b = Math.floor((0.40 + 0.04 * Math.sin(t * Math.PI * 2)) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let i = 0; i < 12; i++) {
      g.fillStyle(0xa09058, 0.3);
      g.fillCircle((i * 19 + 7) % S, (i * 15 + 3) % S, 1.5 + (i % 3));
    }
    g.generateTexture('tile_sand', S, S);
    g.destroy();
  }

  private generatePathTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    for (let y = 0; y < S; y++) {
      const t = y / S;
      const r = Math.floor((0.35 + 0.06 * Math.sin(t * Math.PI)) * 255);
      const gr = Math.floor((0.30 + 0.05 * Math.sin(t * Math.PI + 0.5)) * 255);
      const b = Math.floor((0.20 + 0.04 * Math.sin(t * Math.PI * 2)) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(0, y, S, 1);
    }
    for (let i = 0; i < 8; i++) {
      g.fillStyle(0x5a4a30, 0.5);
      g.fillRect((i * 25 + 3) % S, (i * 17 + 5) % S, 6 + (i % 4), 4 + (i % 3));
    }
    g.generateTexture('tile_path', S, S);
    g.destroy();
  }

  private generateSpawnTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    const C = S / 2;
    const R = C - 2;

    // Outer glow ring
    g.lineStyle(2, 0xff4400, 0.3); g.strokeCircle(C, C, R + 1);
    // Thick outer ring
    g.lineStyle(4, 0xff3300, 1); g.strokeCircle(C, C, R);
    // Concentric fiery rings (transparent centre)
    for (let i = 0; i < 4; i++) {
      g.fillStyle(0xff2200 + i * 0x331100, 0.35 - i * 0.06);
      g.fillCircle(C, C, R - i * 5);
    }
    // Bright core
    g.fillStyle(0xff6600, 0.8);
    g.fillCircle(C, C, R * 0.3);
    // Spawn-direction arrow (points north)
    g.fillStyle(0xffaa44, 0.9);
    g.fillTriangle(C, C - R * 0.55, C + 5, C - R * 0.2, C - 5, C - R * 0.2);
    g.generateTexture('tile_spawn', S, S);
    g.destroy();
  }

  private generateGoalTile() {
    const g = this.add.graphics();
    const S = TILE_SIZE;
    const C = S / 2;
    const R = C - 2;

    // Outer glow ring
    g.lineStyle(2, 0x00ff66, 0.3); g.strokeCircle(C, C, R + 1);
    // Thick outer ring
    g.lineStyle(4, 0x00ff66, 1); g.strokeCircle(C, C, R);
    // Concentric green rings (transparent centre)
    for (let i = 0; i < 3; i++) {
      g.fillStyle(0x008833 + i * 0x004422, 0.35 - i * 0.08);
      g.fillCircle(C, C, R - i * 5);
    }
    // Bright golden core star shape
    g.fillStyle(0xffd700, 0.9);
    g.fillCircle(C, C, R * 0.25);
    // Crosshair / compass lines
    g.lineStyle(2, 0xffd700, 0.7);
    g.strokeCircle(C, C, R * 0.45);
    g.lineBetween(C - R * 0.5, C, C + R * 0.5, C);
    g.lineBetween(C, C - R * 0.5, C, C + R * 0.5);
    g.generateTexture('tile_goal', S, S);
    g.destroy();
  }

  // ─── Blob tileset ─────────────────────────────────────────────────────────
  private generateBlobTextures() {
    generateBlobTextures(this);
    // Generate alpha-only path overlays for grass Wang tile underneath
    generatePathOverlayTextures(this);
  }

  // ─── Terrain transition tiles ─────────────────────────────────────────────
  private generateTerrainTransitionTextures() {
    // Always generate transition textures. If an AI grass texture was loaded,
    // pass its key as the source so it gets baked into the transition polygons.
    const grassSource = this.textures.exists('tile_grass') ? 'tile_grass' : undefined;
    generateTransitionTextures(this, 'grass', grassSource);

    // Generate alpha-mask overlays for edge grass cells. These are transparent
    // in the grass-blob area and show water outside, so they can be rendered
    // on top of a sharp 48×48 Wang tile to hide the grass in the water zone.
    if (this.textures.exists('tile_ground')) {
      generateTransitionAlphaMasks(this, 'grass', 'tile_ground');
    }

    // Spawn and goal tiles get the same SDF-based corner blending so their
    // water-facing edges blend organically instead of showing hard squares.
    const spawnSource = this.textures.exists('tile_spawn') ? 'tile_spawn' : undefined;
    generateTransitionTextures(this, 'spawn', spawnSource);
    const goalSource = this.textures.exists('tile_goal') ? 'tile_goal' : undefined;
    generateTransitionTextures(this, 'goal', goalSource);
  }

  // ─── Towers ───────────────────────────────────────────────────────────────
  private generateTowerTextures() {
    const S = 44;
    const C = S / 2;
    const g = this.add.graphics();

    for (const type of TOWER_TYPES_ORDERED) {
      const def = TOWER_DEFS[type];

      // ── Base levels 0–2 ─────────────────────────────────────────────────
      [0, 1, 2].forEach(level => {
        const key = `tower_${type}_${level}`;
        if (this.aiLoadedTiles.has(key)) return; // AI texture loaded successfully

        const scale = 1 + level * 0.10;
        const R = Math.round(13 * scale);
        const color = def.color;
        g.clear();

        // Ground plate
        g.fillStyle(0x223344, 1);
        g.fillCircle(C, C + 2, R + 5);
        // Shadow
        g.fillStyle(0x000000, 0.3);
        g.fillEllipse(C, C + R + 3, R * 2, 6);
        // Base
        g.fillStyle(COLORS.TOWER_BASE, 1);
        g.fillCircle(C, C, R + 4);
        g.lineStyle(2, 0x445566, 0.8);
        g.strokeCircle(C, C, R + 4);
        // Body
        g.fillStyle(color, 1);
        g.lineStyle(2, 0xffffff, 0.3);
        switch (def.drawShape) {
          case 'triangle':
            g.fillTriangle(C, C - R, C + R * 0.9, C + R * 0.7, C - R * 0.9, C + R * 0.7);
            g.strokeTriangle(C, C - R, C + R * 0.9, C + R * 0.7, C - R * 0.9, C + R * 0.7);
            // Barrel
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(20).color, 1);
            g.fillRect(C - 3, C - R, 6, R * 0.6);
            break;
          case 'circle':
            g.fillCircle(C, C, R);
            g.strokeCircle(C, C, R);
            // Inner glow
            g.fillStyle(0xffffff, 0.15);
            g.fillCircle(C - R * 0.25, C - R * 0.25, R * 0.4);
            // Cannon barrel
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(30).color, 1);
            g.fillRect(C - 4, C - R - 6, 8, R * 0.7);
            break;
          case 'diamond':
            g.fillPoints([
              { x: C, y: C - R }, { x: C + R, y: C },
              { x: C, y: C + R }, { x: C - R, y: C },
            ], true);
            // Crystal lines
            g.lineStyle(1, 0xffffff, 0.4);
            g.lineBetween(C, C - R, C + R, C);
            g.lineBetween(C, C - R, C - R, C);
            break;
          case 'square':
            g.fillRoundedRect(C - R + 2, C - R + 2, R * 2 - 4, R * 2 - 4, 3);
            g.strokeRoundedRect(C - R + 2, C - R + 2, R * 2 - 4, R * 2 - 4, 3);
            // X cross rivet
            g.lineStyle(2, Phaser.Display.Color.IntegerToColor(color).darken(20).color, 1);
            g.lineBetween(C - R + 5, C, C + R - 5, C);
            g.lineBetween(C, C - R + 5, C, C + R - 5);
            break;
          case 'star': {
            const pts = this.starPoints(C, C, R, R * 0.5, 5);
            g.fillPoints(pts, true);
            g.strokePoints(pts, true);
            // Inner ring
            g.fillStyle(0xffffff, 0.2);
            g.fillCircle(C, C, R * 0.3);
            break;
          }
          case 'hex': {
            const pts = this.hexPoints(C, C, R);
            g.fillPoints(pts, true);
            g.strokePoints(pts, true);
            // Hex center pattern
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(25).color, 0.8);
            const inner = this.hexPoints(C, C, R * 0.55);
            g.fillPoints(inner, true);
            break;
          }
        }

        // Level pips
        for (let i = 0; i < level; i++) {
          g.fillStyle(0xffd700, 0.95);
          g.fillCircle(C - (level - 1) * 4 + i * 8, C + R + 6, 3);
        }

        g.generateTexture(key, S, S);
      });

      // ── Evolution textures ──────────────────────────────────────────────
      def.evolutions.forEach((evo) => {
        const key = `tower_${evo.type}`;
        if (this.aiLoadedTiles.has(key)) return; // AI texture loaded successfully

        const R = Math.round(13 * 1.30);
        const color = evo.color;
        g.clear();

        // Ground plate (amber-tinted for evolved)
        g.fillStyle(0x332200, 1);
        g.fillCircle(C, C + 2, R + 5);
        // Shadow
        g.fillStyle(0x000000, 0.3);
        g.fillEllipse(C, C + R + 3, R * 2, 6);
        // Base with gold rim to signal evolution
        g.fillStyle(COLORS.TOWER_BASE, 1);
        g.fillCircle(C, C, R + 4);
        g.lineStyle(3, 0xffd700, 0.9);
        g.strokeCircle(C, C, R + 4);
        // Body
        g.fillStyle(color, 1);
        g.lineStyle(2, 0xffffff, 0.4);
        switch (def.drawShape) {
          case 'triangle':
            g.fillTriangle(C, C - R, C + R * 0.9, C + R * 0.7, C - R * 0.9, C + R * 0.7);
            g.strokeTriangle(C, C - R, C + R * 0.9, C + R * 0.7, C - R * 0.9, C + R * 0.7);
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(20).color, 1);
            g.fillRect(C - 3, C - R, 6, R * 0.6);
            break;
          case 'circle':
            g.fillCircle(C, C, R);
            g.strokeCircle(C, C, R);
            g.fillStyle(0xffffff, 0.15);
            g.fillCircle(C - R * 0.25, C - R * 0.25, R * 0.4);
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(30).color, 1);
            g.fillRect(C - 4, C - R - 6, 8, R * 0.7);
            break;
          case 'diamond':
            g.fillPoints([
              { x: C, y: C - R }, { x: C + R, y: C },
              { x: C, y: C + R }, { x: C - R, y: C },
            ], true);
            g.lineStyle(1, 0xffffff, 0.4);
            g.lineBetween(C, C - R, C + R, C);
            g.lineBetween(C, C - R, C - R, C);
            break;
          case 'square':
            g.fillRoundedRect(C - R + 2, C - R + 2, R * 2 - 4, R * 2 - 4, 3);
            g.strokeRoundedRect(C - R + 2, C - R + 2, R * 2 - 4, R * 2 - 4, 3);
            g.lineStyle(2, Phaser.Display.Color.IntegerToColor(color).darken(20).color, 1);
            g.lineBetween(C - R + 5, C, C + R - 5, C);
            g.lineBetween(C, C - R + 5, C, C + R - 5);
            break;
          case 'star': {
            const pts = this.starPoints(C, C, R, R * 0.5, 5);
            g.fillPoints(pts, true);
            g.strokePoints(pts, true);
            g.fillStyle(0xffffff, 0.2);
            g.fillCircle(C, C, R * 0.3);
            break;
          }
          case 'hex': {
            const pts = this.hexPoints(C, C, R);
            g.fillPoints(pts, true);
            g.strokePoints(pts, true);
            g.fillStyle(Phaser.Display.Color.IntegerToColor(color).darken(25).color, 0.8);
            const inner = this.hexPoints(C, C, R * 0.55);
            g.fillPoints(inner, true);
            break;
          }
        }

        // 3 gold pips — evolution marker
        g.fillStyle(0xffd700, 0.95);
        g.fillCircle(C - 8, C + R + 6, 3.5);
        g.fillCircle(C,     C + R + 6, 3.5);
        g.fillCircle(C + 8, C + R + 6, 3.5);

        g.generateTexture(key, S, S);
      });
    }
    g.destroy();
  }

  // ─── Enemy sprite sheets (4-frame walk cycle, 32×32 per frame) ───────────
  private generateEnemySheets() {
    const FS = 32; // frame size
    const FRAMES = 4;
    const g = this.add.graphics();

    const allDefs = [
      ...Object.values(ENEMY_DEFS),
      ...Object.values(BOSS_DEFS),
    ];

    // Walk cycle squish: [normal, wide/short, normal, narrow/tall]
    const scaleX = [1.0, 1.2, 1.0, 0.85];
    const scaleY = [1.0, 0.85, 1.0, 1.2];

    for (const def of allDefs) {
      const totalW = FS * FRAMES;
      const R = def.isBoss ? 13 : 9;
      g.clear();

      for (let f = 0; f < FRAMES; f++) {
        const cx = f * FS + FS / 2;
        const cy = FS / 2 + (f % 2 === 1 ? 2 : 0); // slight vertical bob
        const sx = scaleX[f];
        const sy = scaleY[f];
        const rx = R * sx;
        const ry = R * sy;

        // Shadow
        g.fillStyle(0x000000, 0.3);
        g.fillEllipse(cx, cy + R + 2, rx * 2, 5);

        // Body
        g.fillStyle(def.color, 1);
        g.lineStyle(2, 0xffffff, 0.5);

        switch (def.drawShape) {
          case 'circle':
            g.fillEllipse(cx, cy, rx * 2, ry * 2);
            g.strokeEllipse(cx, cy, rx * 2, ry * 2);
            // Eyes
            g.fillStyle(0xffffff, 1);
            g.fillCircle(cx - rx * 0.3, cy - ry * 0.15, 2.5);
            g.fillCircle(cx + rx * 0.3, cy - ry * 0.15, 2.5);
            g.fillStyle(0x000000, 1);
            g.fillCircle(cx - rx * 0.3 + 0.5, cy - ry * 0.15, 1.2);
            g.fillCircle(cx + rx * 0.3 + 0.5, cy - ry * 0.15, 1.2);
            break;

          case 'tri': {
            // Runner: sleek arrow shape with speed lines
            const w = rx * 2, h = ry * 2;
            g.fillTriangle(cx + w * 0.5, cy, cx - w * 0.4, cy - h * 0.5, cx - w * 0.4, cy + h * 0.5);
            g.strokeTriangle(cx + w * 0.5, cy, cx - w * 0.4, cy - h * 0.5, cx - w * 0.4, cy + h * 0.5);
            // Speed streaks
            g.lineStyle(1, def.color, 0.5);
            for (let l = 0; l < 3; l++) {
              const ly = cy - h * 0.3 + l * h * 0.3;
              g.lineBetween(cx - w * 0.8 - l * 4, ly, cx - w * 0.4 - l * 4, ly);
            }
            // Eye
            g.fillStyle(0xffffff, 1);
            g.fillCircle(cx + rx * 0.15, cy - 2, 2);
            g.fillStyle(0x000000, 1);
            g.fillCircle(cx + rx * 0.3, cy - 2, 1);
            break;
          }

          case 'square': {
            // Tank: armored blocky shape
            const bw = rx * 2 - 2, bh = ry * 2 - 2;
            g.fillRect(cx - bw/2, cy - bh/2, bw, bh);
            g.strokeRect(cx - bw/2, cy - bh/2, bw, bh);
            // Armor plates
            g.fillStyle(Phaser.Display.Color.IntegerToColor(def.color).darken(25).color, 1);
            g.fillRect(cx - bw * 0.4, cy - bh * 0.45, bw * 0.8, bh * 0.25);
            // Eye slit
            g.fillStyle(0xff4400, 1);
            g.fillRect(cx - bw * 0.3, cy - bh * 0.15, bw * 0.6, bh * 0.12);
            // Treads
            g.fillStyle(0x333333, 1);
            g.fillRect(cx - bw/2, cy + bh * 0.35, bw, bh * 0.15);
            break;
          }

          case 'diamond': {
            // Flyer: wing spread varies by frame
            const wingSpread = [1.0, 1.3, 1.0, 0.7][f];
            const wx2 = rx * wingSpread * 2.2;
            const wy2 = ry * 1.5;
            // Wings
            g.fillStyle(Phaser.Display.Color.IntegerToColor(def.color).lighten(15).color, 0.7);
            g.fillTriangle(cx, cy, cx - wx2/2, cy - wy2/3, cx - wx2/4, cy + wy2/3);
            g.fillTriangle(cx, cy, cx + wx2/2, cy - wy2/3, cx + wx2/4, cy + wy2/3);
            // Body
            g.fillStyle(def.color, 1);
            g.fillPoints([
              { x: cx, y: cy - ry }, { x: cx + rx * 0.6, y: cy },
              { x: cx, y: cy + ry * 0.7 }, { x: cx - rx * 0.6, y: cy },
            ], true);
            // Eyes
            g.fillStyle(0xffffff, 1);
            g.fillCircle(cx - rx * 0.2, cy - ry * 0.15, 2);
            g.fillCircle(cx + rx * 0.2, cy - ry * 0.15, 2);
            break;
          }

          case 'star': {
            // Healer: cross/plus shape with glow
            const arm = ry * 0.5;
            g.fillRect(cx - rx * 0.4, cy - arm - ry * 0.3, rx * 0.8, arm * 2 + ry * 0.6);
            g.fillRect(cx - rx * 0.8, cy - arm * 0.5, rx * 1.6, arm);
            // Cross highlight
            g.fillStyle(0xffffff, 0.5);
            g.fillRect(cx - rx * 0.15, cy - ry * 0.7, rx * 0.3, ry * 1.4);
            // Orb above cross
            g.fillStyle(0x88ffbb, 0.9);
            g.fillCircle(cx, cy - ry * 0.5, rx * 0.3);
            break;
          }
        }

        // Boss: crown indicator
        if (def.isBoss) {
          g.fillStyle(0xffd700, 1);
          const crownPts = [
            { x: cx - rx * 0.6, y: cy - R - 2 },
            { x: cx - rx * 0.3, y: cy - R - 6 },
            { x: cx, y: cy - R - 2 },
            { x: cx + rx * 0.3, y: cy - R - 6 },
            { x: cx + rx * 0.6, y: cy - R - 2 },
          ];
          g.fillPoints(crownPts, false);
        }
      }

      g.generateTexture(`enemy_${def.type}_sheet`, totalW, FS);

      // Add frame data
      const tex = this.textures.get(`enemy_${def.type}_sheet`);
      for (let f = 0; f < FRAMES; f++) {
        tex.add(f, 0, f * FS, 0, FS, FS);
      }
    }

    g.destroy();
  }

  // ─── Hero sprite sheet (40×40 per frame, 4 frames) ───────────────────────
  private generateHeroSheet() {
    const FS = 40;
    const FRAMES = 6; // 4 walk + 1 idle + 1 attack
    const C = FS / 2;
    const g = this.add.graphics();

    const bobY  = [0, -3, 0, 3, 0, -2];
    const swordAngle = [0, -15, 0, 15, 0, 40]; // degrees, last is attack swing

    g.clear();
    for (let f = 0; f < FRAMES; f++) {
      const cx = f * FS + C;
      const cy = C + bobY[f];
      const isAttack = f === 5;

      // Shadow
      g.fillStyle(0x000000, 0.25);
      g.fillEllipse(cx, C + 16, 22, 6);

      // Cape / cloak back
      g.fillStyle(0x3355aa, 0.8);
      g.fillEllipse(cx, cy + 5, 20, 22);

      // Body armor (torso)
      g.fillStyle(0xddaa22, 1);
      g.fillRoundedRect(cx - 8, cy - 10, 16, 18, 3);

      // Armor highlight
      g.fillStyle(0xffcc44, 0.6);
      g.fillRoundedRect(cx - 5, cy - 8, 10, 8, 2);

      // Shoulder pads
      g.fillStyle(0xcc8800, 1);
      g.fillCircle(cx - 9, cy - 8, 5);
      g.fillCircle(cx + 9, cy - 8, 5);

      // Helmet
      g.fillStyle(0xddaa22, 1);
      g.fillCircle(cx, cy - 14, 9);
      // Visor
      g.fillStyle(0x223366, 1);
      g.fillRect(cx - 6, cy - 17, 12, 5);
      // Visor slit
      g.fillStyle(0x4488ff, 0.8);
      g.fillRect(cx - 4, cy - 16, 8, 2);
      // Plume
      g.fillStyle(0xff2222, 0.9);
      for (let p = 0; p < 3; p++) {
        g.fillRect(cx - 2 + p * 2, cy - 23 + (f % 2) * p, 2, 6 - p);
      }

      // Sword
      const sa = swordAngle[f] * (Math.PI / 180);
      const sLen = isAttack ? 20 : 16;
      const sBase = { x: cx + 10, y: cy };
      g.lineStyle(3, 0xcccccc, 1);
      g.lineBetween(
        sBase.x, sBase.y,
        sBase.x + Math.cos(sa) * sLen,
        sBase.y + Math.sin(sa) * sLen
      );
      // Sword guard
      g.fillStyle(0xffaa00, 1);
      g.fillRect(sBase.x - 2, sBase.y - 1, 4, 4);

      // Shield (left side)
      g.fillStyle(0x3355aa, 1);
      g.fillRoundedRect(cx - 16, cy - 8, 8, 14, 2);
      g.lineStyle(1, 0x8899cc, 0.7);
      g.strokeRoundedRect(cx - 16, cy - 8, 8, 14, 2);
      g.fillStyle(0xffaa00, 0.8);
      g.fillCircle(cx - 12, cy, 3);

      // Legs
      const legStep = [0, 5, 0, -5, 0, 0][f];
      g.fillStyle(0x334455, 1);
      g.fillRect(cx - 7, cy + 8, 6, 10 + legStep);
      g.fillRect(cx + 1, cy + 8, 6, 10 - legStep);
      // Boots
      g.fillStyle(0x5a3a1a, 1);
      g.fillRect(cx - 8, cy + 15 + legStep, 7, 5);
      g.fillRect(cx, cy + 15 - legStep, 7, 5);

      // Attack flash effect
      if (isAttack) {
        g.lineStyle(2, 0xffffaa, 0.8);
        g.lineBetween(sBase.x, sBase.y, sBase.x + 22, sBase.y + 8);
        g.lineBetween(sBase.x, sBase.y, sBase.x + 18, sBase.y - 10);
      }
    }

    g.generateTexture('hero_sheet', FS * FRAMES, FS);

    // Add frame data
    const tex = this.textures.get('hero_sheet');
    for (let f = 0; f < FRAMES; f++) {
      tex.add(f, 0, f * FS, 0, FS, FS);
    }

    g.destroy();
  }

  // City decoration textures
  private generateCityDecorTextures() {
    if (!this.aiLoadedTiles.has('deco_city_dense'))    this.generateCityDense();
    if (!this.aiLoadedTiles.has('deco_city_market'))   this.generateCityMarket();
    if (!this.aiLoadedTiles.has('deco_city_harbor'))   this.generateCityHarbor();
    if (!this.aiLoadedTiles.has('deco_city_house_01')) this.generateCityHouse('deco_city_house_01', 0x8B7355, 0x6B3A2A, 0x4A7A9A);
    if (!this.aiLoadedTiles.has('deco_city_house_02')) this.generateCityHouse('deco_city_house_02', 0xA08060, 0x8B4513, 0x3A6A8A);
    if (!this.aiLoadedTiles.has('deco_city_tower'))    this.generateCityTower();
  }

  private generateCityDense() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Base ground
    g.fillStyle(0x3a3a30, 1); g.fillRect(0, 0, S, S);
    // 4-6 small buildings packed together
    const buildings = [
      { x: 3, y: 4, w: 18, h: 16, roof: 0x8B4513, wall: 0xC4A882 },
      { x: 24, y: 2, w: 16, h: 20, roof: 0xA0522D, wall: 0xD4B896 },
      { x: 6, y: 24, w: 14, h: 18, roof: 0x6B3A2A, wall: 0xBFB09A },
      { x: 28, y: 26, w: 16, h: 16, roof: 0x8B6914, wall: 0xC8B898 },
      { x: 14, y: 12, w: 10, h: 22, roof: 0x7B3A1A, wall: 0xD0C0A0 },
      { x: 32, y: 10, w: 12, h: 14, roof: 0x9B5923, wall: 0xC4B492 },
    ];
    for (const b of buildings) {
      // Wall
      g.fillStyle(b.wall, 1);
      g.fillRect(b.x, b.y + 4, b.w, b.h - 4);
      // Roof
      g.fillStyle(b.roof, 1);
      g.fillRect(b.x - 1, b.y, b.w + 2, 5);
      // Window dots
      if (b.w > 8 && b.h > 16) {
        g.fillStyle(0x88CCFF, 0.7);
        g.fillRect(b.x + 4, b.y + 10, 4, 4);
        g.fillRect(b.x + b.w - 8, b.y + 10, 4, 4);
      }
    }
    g.generateTexture('deco_city_dense', S, S);
    g.destroy();
  }

  private generateCityMarket() {
    const S = TILE_SIZE * 2, g = this.add.graphics();
    // Cobblestone ground
    g.fillStyle(0x5a5040, 1); g.fillRect(0, 0, S, S);
    for (let i = 0; i < 20; i++) {
      g.fillStyle((i % 3 === 0) ? 0x6a6050 : 0x5a5040, 0.6);
      g.fillRect((i * 17 + 3) % S, (i * 13 + 5) % S, 5 + (i % 3), 4 + (i % 2));
    }
    // Surrounding buildings (corners)
    const corners = [
      { x: 2, y: 2, w: 22, h: 18, wall: 0xC4B090, roof: 0x8B4513 },
      { x: S - 24, y: 2, w: 22, h: 18, wall: 0xD4C0A0, roof: 0x9B5523 },
      { x: 2, y: S - 20, w: 22, h: 18, wall: 0xC8B898, roof: 0x7B3513 },
      { x: S - 24, y: S - 20, w: 22, h: 18, wall: 0xD0C0A8, roof: 0x8B6523 },
    ];
    for (const b of corners) {
      g.fillStyle(b.wall, 1);
      g.fillRect(b.x, b.y + 3, b.w, b.h - 3);
      g.fillStyle(b.roof, 1);
      g.fillRect(b.x - 1, b.y, b.w + 2, 4);
    }
    // Open center — market stalls (colorful dots)
    const stallColors = [0xff4444, 0x44ff44, 0x4444ff, 0xffff44, 0xff44ff, 0x44ffff];
    for (let i = 0; i < 8; i++) {
      const sx = 30 + (i % 4) * 18;
      const sy = 26 + Math.floor(i / 4) * 22;
      g.fillStyle(stallColors[i % stallColors.length], 0.8);
      g.fillRect(sx, sy, 6, 6);
      // Tiny canopy
      g.fillStyle(0xffffff, 0.4);
      g.fillRect(sx - 2, sy - 2, 10, 3);
    }
    // Fountain in center
    g.fillStyle(0x4488cc, 0.6);
    g.fillCircle(S / 2, S / 2, 8);
    g.lineStyle(1, 0x6699cc, 0.8);
    g.strokeCircle(S / 2, S / 2, 8);
    g.fillStyle(0x88ccff, 0.4);
    g.fillCircle(S / 2, S / 2, 4);

    g.generateTexture('deco_city_market', S, S);
    g.destroy();
  }

  private generateCityHarbor() {
    const S = TILE_SIZE, g = this.add.graphics();
    const W = S * 2, H = S;
    // ── Left half: Land/pier ──
    for (let y = 0; y < H; y++) {
      const t = y / H;
      const r = Math.floor((0.40 + 0.05 * t) * 255);
      const gr = Math.floor((0.35 + 0.04 * t) * 255);
      const b = Math.floor((0.25 + 0.03 * t) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(0, y, S, 1);
    }
    // ── Right half: Water ──
    for (let y = 0; y < H; y++) {
      const t = y / H;
      const r = Math.floor((0.12 + 0.03 * t) * 255);
      const gr = Math.floor((0.22 + 0.04 * t) * 255);
      const b = Math.floor((0.48 + 0.05 * t) * 255);
      g.fillStyle(Phaser.Display.Color.GetColor(r, gr, b), 1);
      g.fillRect(S, y, S, 1);
    }
    // Pier planks (left half)
    g.fillStyle(0x8B7355, 1);
    g.fillRect(8, H * 0.3, S - 16, H * 0.25);
    for (let px = 10; px < S - 10; px += 8) {
      g.fillStyle(0x7a6345, 0.5);
      g.fillRect(px, H * 0.3, 3, H * 0.25);
    }
    // Small boat (right half)
    g.fillStyle(0x6B4226, 1);
    g.fillTriangle(S + S * 0.25, H * 0.78, S + S * 0.15, H * 0.68, S + S * 0.35, H * 0.68);
    // Mast
    g.lineStyle(1, 0x333333, 1);
    g.lineBetween(S + S * 0.25, H * 0.68, S + S * 0.25, H * 0.55);
    // Sail
    g.fillStyle(0xffffff, 0.7);
    g.fillTriangle(S + S * 0.26, H * 0.56, S + S * 0.26, H * 0.66, S + S * 0.36, H * 0.62);
    // Crates (left half)
    g.fillStyle(0xA08060, 1);
    g.fillRect(20, 8, 8, 8);
    g.fillStyle(0x8B7355, 1);
    g.fillRect(16, 10, 8, 8);

    g.generateTexture('deco_city_harbor', W, H);
    g.destroy();
  }

  private generateCityHouse(key: string, wallColor: number, roofColor: number, doorColor: number) {
    const S = TILE_SIZE, g = this.add.graphics();
    // Ground
    g.fillStyle(0x3a3a30, 1); g.fillRect(0, 0, S, S);
    // Wall
    g.fillStyle(wallColor, 1);
    g.fillRect(8, 18, S - 16, S - 26);
    // Roof (triangle)
    g.fillStyle(roofColor, 1);
    g.fillTriangle(S / 2, 3, 4, 20, S - 4, 20);
    // Roof highlight
    g.fillStyle(Phaser.Display.Color.IntegerToColor(roofColor).lighten(10).color, 0.6);
    g.fillTriangle(S / 2, 3, S / 2 + 10, 20, S - 4, 20);
    // Door
    g.fillStyle(doorColor, 1);
    g.fillRect(S / 2 - 5, S - 16, 10, 14);
    // Door handle
    g.fillStyle(0xffd700, 0.8);
    g.fillCircle(S / 2 + 3, S - 10, 1.5);
    // Window
    g.fillStyle(0x88CCFF, 0.6);
    g.fillRect(S / 2 + 8, 24, 8, 8);
    g.lineStyle(1, wallColor, 0.5);
    g.strokeRect(S / 2 + 8, 24, 8, 8);
    // Window cross
    g.lineBetween(S / 2 + 12, 24, S / 2 + 12, 32);
    g.lineBetween(S / 2 + 8, 28, S / 2 + 16, 28);
    // Small chimney
    g.fillStyle(0x666666, 1);
    g.fillRect(S / 2 - 10, 8, 4, 10);

    g.generateTexture(key, S, S);
    g.destroy();
  }

  private generateCityTower() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Ground
    g.fillStyle(0x3a3a30, 1); g.fillRect(0, 0, S, S);
    // Circular base
    g.fillStyle(0x5a5a5a, 1);
    g.fillCircle(S / 2, S / 2 + 2, 18);
    // Tower body
    g.fillStyle(0x7a7a7a, 1);
    g.fillCircle(S / 2, S / 2 - 2, 14);
    // Stone texture lines
    g.lineStyle(1, 0x666666, 0.4);
    g.strokeCircle(S / 2, S / 2 - 2, 14);
    g.lineBetween(S / 2 - 14, S / 2 - 2, S / 2 + 14, S / 2 - 2);
    g.lineBetween(S / 2 - 10, S / 2 - 10, S / 2 + 10, S / 2 + 6);
    // Conical roof
    g.fillStyle(0x3355aa, 1);
    g.fillTriangle(S / 2, 4, S / 2 - 16, S / 2 - 4, S / 2 + 16, S / 2 - 4);
    // Roof highlight
    g.fillStyle(0x4477cc, 0.5);
    g.fillTriangle(S / 2, 4, S / 2, S / 2 - 4, S / 2 + 16, S / 2 - 4);
    // Flag on top
    g.lineStyle(1, 0x333333, 1);
    g.lineBetween(S / 2, 4, S / 2, 0);
    g.fillStyle(0xff4444, 0.8);
    g.fillTriangle(S / 2 + 1, 0, S / 2 + 1, 4, S / 2 + 6, 2);
    // Window slits
    g.fillStyle(0x333333, 1);
    g.fillRect(S / 2 - 6, S / 2 - 6, 4, 8);
    g.fillRect(S / 2 + 2, S / 2 - 6, 4, 8);

    g.generateTexture('deco_city_tower', S, S);
    g.destroy();
  }

  // ─── Economy building textures ──────────────────────────────────────────

  private generateEconomyTextures() {
    if (!this.aiLoadedTiles.has('eco_farm'))       this.generateEcoFarm();
    if (!this.aiLoadedTiles.has('eco_mill'))       this.generateEcoMill();
    if (!this.aiLoadedTiles.has('eco_bakery'))     this.generateEcoBakery();
    if (!this.aiLoadedTiles.has('eco_fishery'))    this.generateEcoFishery();
    if (!this.aiLoadedTiles.has('eco_harbor'))     this.generateEcoHarbor();
    if (!this.aiLoadedTiles.has('eco_warehouse'))  this.generateEcoWarehouse();
    if (!this.aiLoadedTiles.has('eco_market'))     this.generateEcoMarket();
    if (!this.aiLoadedTiles.has('eco_lighthouse')) this.generateEcoLighthouse();
    if (!this.aiLoadedTiles.has('eco_cart'))       this.generateEcoCart();
  }

  private generateEcoFarm() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Dirt field
    g.fillStyle(0x5a4a20, 1); g.fillRect(0, 0, S, S);
    // Crop rows
    for (let row = 0; row < 4; row++) {
      const y = 10 + row * 9;
      g.fillStyle(0x6a8a30, 1);
      g.fillRect(4, y, S - 8, 5);
      // Crop dots
      g.fillStyle(0x8aba40, 1);
      for (let x = 6; x < S - 6; x += 7) {
        g.fillCircle(x, y + 2, 2);
      }
    }
    // Fence border
    g.lineStyle(1, 0x8B7355, 1);
    g.strokeRect(2, 2, S - 4, S - 4);
    // Gate (bottom)
    g.lineStyle(1, 0x8B7355, 0); // clear
    g.fillStyle(0x8B7355, 0.6);
    g.fillRect(S / 2 - 4, S - 6, 8, 4);
    // Tiny barn
    g.fillStyle(0xA08060, 1);
    g.fillRect(S - 14, 2, 10, 10);
    g.fillStyle(0x8B4513, 1);
    g.fillTriangle(S - 16, 2, S - 9, -3, S - 2, 2);

    g.generateTexture('eco_farm', S, S);
    g.destroy();
  }

  private generateEcoMill() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Stone base
    g.fillStyle(0x7a7a70, 1);
    g.fillCircle(S / 2, S / 2, 15);
    g.fillStyle(0x8a8a80, 1);
    g.fillCircle(S / 2, S / 2, 10);
    // Door
    g.fillStyle(0x5a3a1a, 1);
    g.fillRect(S / 2 - 4, S / 2 + 3, 8, 10);
    // Windmill blades (cross)
    g.lineStyle(2, 0x8B7355, 1);
    // Vertical blade
    g.lineBetween(S / 2, S / 2 - 18, S / 2, S / 2 - 6);
    // Horizontal blade
    g.lineBetween(S / 2 - 18, S / 2, S / 2 + 18, S / 2);
    // Sail cloth on blades
    g.fillStyle(0xeeeedd, 0.7);
    g.fillRect(S / 2 - 1, S / 2 - 17, 3, 8);
    g.fillRect(S / 2 + 6, S / 2 - 1, 10, 3);
    g.fillRect(S / 2 - 16, S / 2 - 1, 8, 3);
    // Center hub
    g.fillStyle(0x666666, 1);
    g.fillCircle(S / 2, S / 2, 3);

    g.generateTexture('eco_mill', S, S);
    g.destroy();
  }

  private generateEcoBakery() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Stone base
    g.fillStyle(0x9a8a70, 1);
    g.fillRoundedRect(8, 14, S - 16, S - 20, 4);
    // Roof
    g.fillStyle(0x8B4513, 1);
    g.fillTriangle(S / 2, 2, 6, 16, S - 6, 16);
    // Chimney
    g.fillStyle(0x666666, 1);
    g.fillRect(S - 18, 4, 6, 14);
    // Smoke particles (simple dots)
    g.fillStyle(0xcccccc, 0.5);
    g.fillCircle(S - 15, 0, 3);
    g.fillStyle(0xcccccc, 0.3);
    g.fillCircle(S - 12, -3, 2);
    // Door
    g.fillStyle(0x5a3a1a, 1);
    g.fillRoundedRect(S / 2 - 5, S / 2 + 2, 10, S / 2 - 8, 3);
    // Window with glow
    g.fillStyle(0xffcc66, 0.6);
    g.fillRoundedRect(S / 2 - 12, S / 2 - 6, 8, 8, 2);
    // Bread sign
    g.fillStyle(0xd4a843, 0.9);
    g.fillCircle(S / 2 + 12, 12, 5);

    g.generateTexture('eco_bakery', S, S);
    g.destroy();
  }

  private generateEcoFishery() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Grass ground
    g.fillStyle(0x3a5a2a, 1); g.fillRect(0, 0, S, S);
    // Hut
    g.fillStyle(0x9a8a70, 1);
    g.fillRoundedRect(6, 14, S - 20, S - 20, 3);
    // Roof
    g.fillStyle(0x6B4226, 1);
    g.fillTriangle(S / 2 - 8, 4, 4, 16, S - 22, 16);
    // Door
    g.fillStyle(0x4a2a1a, 1);
    g.fillRect(S / 2 - 6, S / 2 + 2, 8, 12);
    // Fishing rod (right side, extending out)
    g.lineStyle(1.5, 0x8B7355, 1);
    g.lineBetween(S - 6, 20, S + 8, 6);
    // Line dropping down
    g.lineStyle(0.5, 0xcccccc, 0.8);
    g.lineBetween(S + 8, 6, S + 8, 18);
    // Bobber
    g.fillStyle(0xff4444, 0.8);
    g.fillCircle(S + 8, 18, 2);
    // Fish bucket
    g.fillStyle(0x888888, 0.7);
    g.fillRect(8, S - 12, 10, 8);
    g.fillStyle(0x6699cc, 0.6);
    g.fillRect(9, S - 10, 8, 4);

    g.generateTexture('eco_fishery', S, S);
    g.destroy();
  }

  private generateEcoHarbor() {
    // Reuse the decorative harbor generator — same texture layout
    const W = TILE_SIZE * 2, H = TILE_SIZE;
    const g = this.add.graphics();
    // Land half
    for (let y = 0; y < H; y++) {
      const t = y / H;
      g.fillStyle(Phaser.Display.Color.GetColor(
        Math.floor((0.40 + 0.05 * t) * 255),
        Math.floor((0.35 + 0.04 * t) * 255),
        Math.floor((0.25 + 0.03 * t) * 255),
      ), 1);
      g.fillRect(0, y, TILE_SIZE, 1);
    }
    // Water half
    for (let y = 0; y < H; y++) {
      const t = y / H;
      g.fillStyle(Phaser.Display.Color.GetColor(
        Math.floor((0.12 + 0.03 * t) * 255),
        Math.floor((0.22 + 0.04 * t) * 255),
        Math.floor((0.48 + 0.05 * t) * 255),
      ), 1);
      g.fillRect(TILE_SIZE, y, TILE_SIZE, 1);
    }
    // Pier
    g.fillStyle(0x8B7355, 1);
    g.fillRect(8, H * 0.3, TILE_SIZE - 16, H * 0.25);
    for (let px = 10; px < TILE_SIZE - 10; px += 8) {
      g.fillStyle(0x7a6345, 0.5);
      g.fillRect(px, H * 0.3, 3, H * 0.25);
    }
    // Boat
    g.fillStyle(0x6B4226, 1);
    g.fillTriangle(TILE_SIZE + TILE_SIZE * 0.25, H * 0.78, TILE_SIZE + TILE_SIZE * 0.15, H * 0.68, TILE_SIZE + TILE_SIZE * 0.35, H * 0.68);
    g.lineStyle(1, 0x333333, 1);
    g.lineBetween(TILE_SIZE + TILE_SIZE * 0.25, H * 0.68, TILE_SIZE + TILE_SIZE * 0.25, H * 0.55);
    g.fillStyle(0xffffff, 0.7);
    g.fillTriangle(TILE_SIZE + TILE_SIZE * 0.26, H * 0.56, TILE_SIZE + TILE_SIZE * 0.26, H * 0.66, TILE_SIZE + TILE_SIZE * 0.36, H * 0.62);
    // Crates
    g.fillStyle(0xA08060, 1);
    g.fillRect(20, 8, 8, 8);

    g.generateTexture('eco_harbor', W, H);
    g.destroy();
  }

  private generateEcoWarehouse() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Ground
    g.fillStyle(0x5a5040, 1); g.fillRect(0, 0, S, S);
    // Large building
    g.fillStyle(0x887766, 1);
    g.fillRoundedRect(3, 6, S - 6, S - 12, 3);
    // Roof
    g.fillStyle(0x6a5a4a, 1);
    g.fillTriangle(S / 2, 0, 2, 8, S - 2, 8);
    // Loading bay (large door)
    g.fillStyle(0x4a3a2a, 1);
    g.fillRoundedRect(S / 2 - 8, S / 2 + 2, 16, S / 2 - 10, 2);
    // Barrels beside door
    g.fillStyle(0x8B6914, 1);
    g.fillRect(4, S - 14, 8, 10);
    g.fillStyle(0x9B7934, 0.5);
    g.fillRect(5, S - 13, 3, 8);
    g.fillStyle(0x8B6914, 1);
    g.fillRect(S - 12, S - 14, 8, 10);
    // Fill level indicators (vertical bars on side)
    g.fillStyle(0x444444, 0.5);
    g.fillRect(S - 4, 10, 2, S - 18);

    g.generateTexture('eco_warehouse', S, S);
    g.destroy();
  }

  private generateEcoMarket() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Cobblestone ground
    g.fillStyle(0x6a6a50, 1); g.fillRect(0, 0, S, S);
    // Main building — warm sandstone
    g.fillStyle(0xd4b870, 1);
    g.fillRoundedRect(4, 10, S - 8, S - 16, 3);
    // Domed roof
    g.fillStyle(0xc49a50, 1);
    g.fillCircle(S / 2, 10, 10);
    // Entrance arch
    g.fillStyle(0x5a3a1a, 1);
    g.fillRoundedRect(S / 2 - 8, S / 2 + 2, 16, S / 2 - 8, 4);
    // Coin piles (gold circles near entrance)
    g.fillStyle(0xffd700, 0.8);
    g.fillCircle(S / 2 - 14, S - 8, 5);
    g.fillCircle(S / 2 + 12, S - 6, 4);
    g.fillCircle(S / 2 - 8, S - 4, 3);
    // Stall awning (striped)
    g.fillStyle(0xdd4444, 0.7);
    g.fillRect(2, 6, S - 16, 6);
    g.fillStyle(0xeeeeee, 0.7);
    g.fillRect(8, 6, 4, 6);
    g.fillRect(18, 6, 4, 6);
    // Scale icon (justice/trade symbol)
    g.lineStyle(1.5, 0x886644, 0.8);
    g.lineBetween(S / 2, 2, S / 2 + 8, -2);

    g.generateTexture('eco_market', S, S);
    g.destroy();
  }

  private generateEcoLighthouse() {
    const S = TILE_SIZE, g = this.add.graphics();
    // Rocky base
    g.fillStyle(0x5a5a5a, 1);
    g.fillCircle(S / 2, S / 2 + 8, 12);
    g.fillStyle(0x6a6a6a, 0.6);
    g.fillCircle(S / 2 - 3, S / 2 + 5, 6);
    // Tower body (tapered)
    g.fillStyle(0xeeeedd, 1);
    g.fillRect(S / 2 - 6, 10, 12, S - 24);
    // Red stripes
    g.fillStyle(0xdd3333, 1);
    g.fillRect(S / 2 - 6, 14, 12, 5);
    g.fillRect(S / 2 - 6, 26, 12, 5);
    // Lantern room
    g.fillStyle(0x333333, 1);
    g.fillRect(S / 2 - 7, 6, 14, 8);
    // Light glow
    g.fillStyle(0xffff88, 0.8);
    g.fillCircle(S / 2, 10, 4);
    g.fillStyle(0xffff88, 0.3);
    g.fillCircle(S / 2, 10, 8);
    // Light beam (cone)
    g.fillStyle(0xffff88, 0.15);
    g.fillTriangle(S / 2 + 4, 8, S / 2 + 4, 12, S, -4);
    // Door at base
    g.fillStyle(0x4a3a2a, 1);
    g.fillRect(S / 2 - 3, S - 10, 6, 8);

    g.generateTexture('eco_lighthouse', S, S);
    g.destroy();
  }

  private generateEcoCart() {
    const W = 20, H = 14, g = this.add.graphics();
    // Cart body
    g.fillStyle(0x8B6914, 1);
    g.fillRoundedRect(1, 2, W - 10, H - 4, 2);
    // Wheels
    g.fillStyle(0x444444, 1);
    g.fillCircle(5, H - 2, 3);
    g.fillCircle(W - 4, H - 2, 3);
    // Wheel spokes
    g.lineStyle(0.5, 0x888888, 0.8);
    g.lineBetween(5, H - 5, 5, H + 1);
    g.lineBetween(2, H - 2, 8, H - 2);
    g.lineBetween(W - 7, H - 5, W - 1, H + 1);
    g.lineBetween(W - 7, H - 2, W - 1, H - 2);
    // Handle/shaft
    g.lineStyle(1, 0x6B4226, 1);
    g.lineBetween(W - 8, 4, W - 1, 4);
    // Cargo (colored box — tint applied at runtime per resource type)
    g.fillStyle(0xd4a843, 0.8);
    g.fillRect(2, 0, W - 14, 5);

    g.generateTexture('eco_cart', W, H);
    g.destroy();
  }

  // ─── 
  // ─── Projectiles ─────────────────────────────────────────────────────────
  private generateProjectileTextures() {
    const g = this.add.graphics();

    // Arrow
    g.clear();
    g.fillStyle(0xcccccc, 1);
    g.fillRect(0, 2, 12, 2); // shaft
    g.fillStyle(COLORS.TOWER_ARROW, 1);
    g.fillTriangle(10, 0, 16, 3, 10, 6); // head
    g.fillStyle(0x888888, 0.7);
    g.fillTriangle(0, 1, 4, 3, 0, 5); // fletching
    g.generateTexture('proj_arrow', 16, 6);

    // Cannon ball - glowing sphere
    g.clear();
    g.fillStyle(0x111111, 1);
    g.fillCircle(6, 6, 6);
    g.fillStyle(COLORS.TOWER_CANNON, 0.7);
    g.fillCircle(6, 6, 4);
    g.fillStyle(0xffaa44, 0.5);
    g.fillCircle(4, 4, 2);
    g.generateTexture('proj_cannon', 12, 12);

    // Ice shard - crystal
    g.clear();
    g.fillStyle(0xaaeeff, 1);
    g.fillPoints([{x:4,y:0},{x:8,y:4},{x:4,y:8},{x:0,y:4}], true);
    g.fillStyle(0xffffff, 0.5);
    g.fillPoints([{x:4,y:0},{x:6,y:3},{x:4,y:2}], true);
    g.generateTexture('proj_ice', 8, 8);

    // Lightning bolt
    g.clear();
    g.fillStyle(COLORS.TOWER_LIGHTNING, 1);
    g.fillPoints([{x:6,y:0},{x:2,y:5},{x:4,y:5},{x:0,y:10},{x:5,y:4},{x:3,y:4}], true);
    g.generateTexture('proj_lightning', 7, 10);

    // Poison orb
    g.clear();
    g.fillStyle(0x224400, 1);
    g.fillCircle(5, 5, 5);
    g.fillStyle(COLORS.TOWER_POISON, 0.8);
    g.fillCircle(5, 5, 4);
    g.fillStyle(0x99ff99, 0.4);
    g.fillCircle(3, 3, 2);
    // Drip
    g.fillStyle(COLORS.TOWER_POISON, 0.6);
    g.fillTriangle(4, 10, 6, 10, 5, 14);
    g.generateTexture('proj_poison', 10, 14);

    // Boomerang - crescent shape
    g.clear();
    g.fillStyle(COLORS.TOWER_BOOMERANG, 1);
    // Outer arc
    g.fillEllipse(8, 4, 14, 6);
    // Cut out inner part
    g.fillStyle(0x000000, 1); // background
    // Instead draw two lines
    g.fillStyle(COLORS.TOWER_BOOMERANG, 1);
    g.fillRect(0, 0, 16, 3);
    g.fillRect(0, 5, 16, 3);
    g.generateTexture('proj_boomerang', 16, 8);

    g.destroy();
  }

  // ─── Particles ─────────────────────────────────────────────────────────
  private generateParticleTextures() {
    const g = this.add.graphics();
    const p: Array<{ key: string; color: number; r: number }> = [
      { key: 'particle_spark',   color: 0xffffaa, r: 4 },
      { key: 'particle_ice',     color: COLORS.FX_FREEZE, r: 5 },
      { key: 'particle_fire',    color: COLORS.FX_METEOR, r: 6 },
      { key: 'particle_poison',  color: COLORS.FX_POISON_CLOUD, r: 5 },
      { key: 'particle_heal',    color: COLORS.FX_HEAL,   r: 4 },
      { key: 'particle_smoke',   color: 0x666666, r: 8 },
      { key: 'particle_exp',     color: COLORS.FX_EXPLOSION, r: 7 },
      { key: 'particle_blood',   color: 0xcc2200, r: 4 },
      { key: 'particle_star',    color: 0xffdd44, r: 5 },
    ];
    for (const pt of p) {
      g.clear();
      // Gradient-like soft circle
      g.fillStyle(0xffffff, 0.3);
      g.fillCircle(pt.r, pt.r, pt.r);
      g.fillStyle(pt.color, 1);
      g.fillCircle(pt.r, pt.r, pt.r * 0.7);
      g.generateTexture(pt.key, pt.r * 2, pt.r * 2);
    }
    g.destroy();
  }

  // ─── UI ──────────────────────────────────────────────────────────────────
  private generateUITextures() {
    const g = this.add.graphics();

    // Panel bg
    g.clear();
    g.fillStyle(COLORS.PANEL_BG, 0.95);
    g.fillRoundedRect(0, 0, 256, 128, 8);
    g.lineStyle(1, COLORS.PANEL_BORDER, 1);
    g.strokeRoundedRect(0, 0, 256, 128, 8);
    g.generateTexture('panel_bg', 256, 128);

    // Range ring
    g.clear();
    g.lineStyle(1, COLORS.TOWER_RANGE, 0.25);
    g.strokeCircle(100, 100, 98);
    g.generateTexture('range_ring', 200, 200);

    // Barricade
    g.clear();
    g.fillStyle(0x5a3510, 1);
    g.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
    // Planks
    for (let p2 = 0; p2 < 3; p2++) {
      const y = p2 * (TILE_SIZE / 3) + 3;
      g.fillStyle(0x7a5520, 1);
      g.fillRect(3, y, TILE_SIZE - 6, TILE_SIZE / 3 - 4);
      // Wood grain
      g.lineStyle(1, 0x4a2a08, 0.5);
      g.lineBetween(6, y + 3, TILE_SIZE - 6, y + 3);
    }
    // Iron bands
    g.fillStyle(0x445566, 1);
    g.fillRect(TILE_SIZE / 2 - 2, 2, 4, TILE_SIZE - 4);
    g.fillRect(2, TILE_SIZE / 2 - 2, TILE_SIZE - 4, 4);
    g.lineStyle(2, 0xaa7755, 1);
    g.strokeRect(0, 0, TILE_SIZE, TILE_SIZE);
    g.generateTexture('barricade', TILE_SIZE, TILE_SIZE);

    g.destroy();
  }

  // ─── Register all Phaser animations ──────────────────────────────────────
  private registerAnimations() {
    const allDefs = [
      ...Object.values(ENEMY_DEFS),
      ...Object.values(BOSS_DEFS),
    ];

    for (const def of allDefs) {
      const key = `enemy_${def.type}_sheet`;
      this.anims.create({
        key: `enemy_${def.type}_walk`,
        frames: [0, 1, 2, 3].map(f => ({ key, frame: f })),
        frameRate: def.isBoss ? 4 : 6,
        repeat: -1,
      });
    }

    // Hero animations
    this.anims.create({
      key: 'hero_idle',
      frames: [{ key: 'hero_sheet', frame: 4 }],
      frameRate: 1,
      repeat: -1,
    });
    this.anims.create({
      key: 'hero_walk',
      frames: [0, 1, 2, 3].map(f => ({ key: 'hero_sheet', frame: f })),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: 'hero_attack',
      frames: [{ key: 'hero_sheet', frame: 5 }],
      frameRate: 12,
      repeat: 0,
    });
  }

  // ─── Ability Icons ────────────────────────────────────────────────────────
  /**
   * Generate procedural ability icon textures for any abilities whose AI
   * texture did not load from disk.
   */
  private generateAbilityIcons() {
    if (!this.textures.exists('ability_freeze'))           this.generateProceduralAbilityIcon('freeze');
    if (!this.textures.exists('ability_meteor'))           this.generateProceduralAbilityIcon('meteor');
    if (!this.textures.exists('ability_lightning_storm'))  this.generateProceduralAbilityIcon('lightning_storm');
    if (!this.textures.exists('ability_heal_aura'))        this.generateProceduralAbilityIcon('heal_aura');
  }

  private generateProceduralAbilityIcon(type: string) {
    const g = this.add.graphics();
    const S = 128; // icon size (downscaled at render time)
    const cx = S / 2;
    const cy = S / 2;
    const r = S * 0.38;

    switch (type) {
      case 'freeze': {
        // Snowflake — 6 arms with branches
        g.fillStyle(0x0a1a3a, 1);
        g.fillCircle(cx, cy, r + 4);
        g.lineStyle(3, 0x99ddff, 0.9);
        for (let a = 0; a < 6; a++) {
          const angle = (a * Math.PI) / 3;
          const ax = cx + Math.cos(angle) * r;
          const ay = cy + Math.sin(angle) * r;
          g.lineBetween(cx, cy, ax, ay);
          // Side branches
          const mx = cx + Math.cos(angle) * r * 0.55;
          const my = cy + Math.sin(angle) * r * 0.55;
          const branchLen = r * 0.28;
          for (const side of [-1, 1]) {
            g.lineBetween(mx, my,
              mx + Math.cos(angle + side * 0.7) * branchLen,
              my + Math.sin(angle + side * 0.7) * branchLen);
          }
        }
        g.fillStyle(0xccffff, 1);
        g.fillCircle(cx, cy, 4);
        break;
      }
      case 'meteor': {
        // Meteor — fiery triangle with core
        g.fillStyle(0x2a0800, 1);
        g.fillCircle(cx, cy, r + 4);
        // Trail triangle
        const ax = Math.PI * 0.75;
        g.fillStyle(0xff5500, 0.9);
        g.fillTriangle(
          cx + Math.cos(ax) * r * 1.3, cy + Math.sin(ax) * r * 1.3,
          cx + Math.cos(ax + 2.6) * r * 0.5, cy + Math.sin(ax + 2.6) * r * 0.5,
          cx + Math.cos(ax - 2.6) * r * 0.5, cy + Math.sin(ax - 2.6) * r * 0.5,
        );
        // Flame core
        g.fillStyle(0xffcc44, 0.9);
        g.fillCircle(cx, cy, r * 0.5);
        // Hot spot
        g.fillStyle(0xffffff, 0.6);
        g.fillCircle(cx - r * 0.12, cy - r * 0.12, r * 0.18);
        break;
      }
      case 'lightning_storm': {
        // Lightning bolt — zigzag shape
        g.fillStyle(0x160a28, 1);
        g.fillCircle(cx, cy, r + 4);
        g.fillStyle(0xffff00, 0.9);
        const pts = [
          { x: cx + r * 0.1, y: cy - r },
          { x: cx - r * 0.05, y: cy - r * 0.1 },
          { x: cx + r * 0.08, y: cy - r * 0.1 },
          { x: cx - r * 0.12, y: cy + r },
          { x: cx + r * 0.03, y: cy + r * 0.08 },
          { x: cx - r * 0.08, y: cy + r * 0.08 },
        ];
        g.fillPoints(pts, true);
        // Bright tip
        g.fillStyle(0xffffff, 0.5);
        g.fillTriangle(
          cx + r * 0.1, cy - r,
          cx - r * 0.05, cy - r * 0.1,
          cx + r * 0.03, cy - r * 0.2,
        );
        break;
      }
      case 'heal_aura': {
        // Temporal Rift — clock/rift with cross motif
        g.fillStyle(0x0a1a0a, 1);
        g.fillCircle(cx, cy, r + 4);
        // Outer ring
        g.lineStyle(3, 0xaa44ff, 0.7);
        g.strokeCircle(cx, cy, r);
        // Cross (X shape)
        g.lineStyle(3, 0x44ff88, 0.7);
        g.lineBetween(cx - r * 0.65, cy - r * 0.65, cx + r * 0.65, cy + r * 0.65);
        g.lineBetween(cx + r * 0.65, cy - r * 0.65, cx - r * 0.65, cy + r * 0.65);
        // Center glow
        g.fillStyle(0xaa44ff, 0.35);
        g.fillCircle(cx, cy, r * 0.45);
        // Clock hand
        g.lineStyle(3, 0xffffff, 0.85);
        g.lineBetween(cx, cy, cx + r * 0.45, cy - r * 0.28);
        g.lineBetween(cx, cy, cx, cy + r * 0.52);
        break;
      }
    }

    g.generateTexture(`ability_${type}`, S, S);
    g.destroy();
  }

  // ─── Shape helpers ────────────────────────────────────────────────────────
  private starPoints(cx: number, cy: number, outerR: number, innerR: number, points: number) {
    const pts: { x: number; y: number }[] = [];
    const step = Math.PI / points;
    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const a = i * step - Math.PI / 2;
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    return pts;
  }

  private hexPoints(cx: number, cy: number, r: number) {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3 - Math.PI / 6;
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    return pts;
  }
}
